import React, { useState } from "react";
import { getClinicalExamples } from "../lib/abgLogic";

const ABGInputForm = ({ onAnalyze, onLoadExample }) => {
  const [values, setValues] = useState({
    pH: 7.4,
    paCO2: 40,
    hco3: 24,
    pO2: 100,
    sO2: 98,
    sodium: 140,
    chloride: 104,
    albumin: 4.0,
  });
  const [isAcute, setIsAcute] = useState(true);
  const examples = getClinicalExamples();

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Keep the value as-is if empty, otherwise parse as float
    // This allows empty fields without defaulting to 0
    if (value === "") {
      setValues({
        ...values,
        [name]: "",
      });
    } else {
      const parsedValue = parseFloat(value);
      setValues({
        ...values,
        [name]: isNaN(parsedValue) ? value : parsedValue,
      });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onAnalyze(values, isAcute);
  };

  const handleExampleSelect = (e) => {
    const selectedIndex = parseInt(e.target.value);
    if (selectedIndex >= 0) {
      const selectedExample = examples[selectedIndex].values;
      setValues(selectedExample);
      onLoadExample(selectedExample);
    }
  };

  return (
    <div className="glass-card p-6 rounded-xl shadow-2xl">
      <h2 className="text-2xl font-bold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">
        ABG Values Input
      </h2>

      <div className="mb-4">
        <label className="block text-gray-300 mb-2 font-medium">
          Load Clinical Example:
        </label>
        <select
          style={{
            color: '#ffffff',
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
            WebkitTextFillColor: '#ffffff'
          }}
          className="w-full p-3 border border-white/20 rounded-lg focus:outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-400/50 transition-all duration-300 backdrop-blur-sm"
          onChange={handleExampleSelect}
          defaultValue=""
        >
          <option value="" disabled className="bg-gray-800">
            Select an example
          </option>
          {examples.map((example, index) => (
            <option key={index} value={index} className="bg-gray-800">
              {example.name}
            </option>
          ))}
        </select>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400 mb-3">
            ABG Parameters
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label htmlFor="ph" className="block text-gray-300 mb-2 font-medium">
                pH
              </label>
              <input
                type="number"
                id="ph"
                name="pH"
                value={values.pH}
                onChange={handleChange}
                step="0.01"
                min="6.8"
                max="7.8"
                style={{
                  color: '#ffffff',
                  backgroundColor: 'rgba(0, 0, 0, 0.4)',
                  WebkitTextFillColor: '#ffffff'
                }}
                className="w-full p-3 border border-white/20 rounded-lg focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-400/50 transition-all duration-300 backdrop-blur-sm"
                required
              />
            </div>

            <div>
              <label htmlFor="paco2" className="block text-gray-300 mb-2 font-medium">
                PaCO2 (mmHg)
              </label>
              <input
                type="number"
                id="paco2"
                name="paCO2"
                value={values.paCO2}
                onChange={handleChange}
                step="0.1"
                min="10"
                max="120"
                style={{
                  color: '#ffffff',
                  backgroundColor: 'rgba(0, 0, 0, 0.4)',
                  WebkitTextFillColor: '#ffffff'
                }}
                className="w-full p-3 border border-white/20 rounded-lg focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-400/50 transition-all duration-300 backdrop-blur-sm"
                required
              />
            </div>

            <div>
              <label htmlFor="hco3" className="block text-gray-300 mb-2 font-medium">
                HCO3- (mEq/L)
              </label>
              <input
                type="number"
                id="hco3"
                name="hco3"
                value={values.hco3}
                onChange={handleChange}
                step="0.1"
                min="5"
                max="50"
                style={{
                  color: '#ffffff',
                  backgroundColor: 'rgba(0, 0, 0, 0.4)',
                  WebkitTextFillColor: '#ffffff'
                }}
                className="w-full p-3 border border-white/20 rounded-lg focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-400/50 transition-all duration-300 backdrop-blur-sm"
                required
              />
            </div>

            <div>
              <label htmlFor="po2" className="block text-gray-300 mb-2 font-medium">
                PaO2 (mmHg) <span className="text-gray-500">(optional)</span>
              </label>
              <input
                type="number"
                id="po2"
                name="pO2"
                value={values.pO2 || ""}
                onChange={handleChange}
                step="0.1"
                min="0"
                max="700"
                style={{
                  color: '#ffffff',
                  backgroundColor: 'rgba(0, 0, 0, 0.4)',
                  WebkitTextFillColor: '#ffffff'
                }}
                className="w-full p-3 border border-white/20 rounded-lg focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-400/50 transition-all duration-300 backdrop-blur-sm"
              />
            </div>

            <div>
              <label htmlFor="so2" className="block text-gray-300 mb-2 font-medium">
                SaO2 (%) <span className="text-gray-500">(optional)</span>
              </label>
              <input
                type="number"
                id="so2"
                name="sO2"
                value={values.sO2 || ""}
                onChange={handleChange}
                step="0.1"
                min="0"
                max="100"
                style={{
                  color: '#ffffff',
                  backgroundColor: 'rgba(0, 0, 0, 0.4)',
                  WebkitTextFillColor: '#ffffff'
                }}
                className="w-full p-3 border border-white/20 rounded-lg focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-400/50 transition-all duration-300 backdrop-blur-sm"
              />
            </div>

            <div className="flex items-center md:col-span-2 bg-white/5 p-4 rounded-lg border border-white/10">
              <label className="block text-gray-300 mr-4 font-medium">
                Respiratory Disorder Duration:
              </label>
              <div className="flex space-x-4">
                <label className="inline-flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="duration"
                    checked={isAcute}
                    onChange={() => setIsAcute(true)}
                    className="form-radio h-4 w-4 text-blue-400 border-white/30 bg-white/10 focus:ring-blue-400"
                  />
                  <span className="ml-2 text-gray-300">Acute</span>
                </label>
                <label className="inline-flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="duration"
                    checked={!isAcute}
                    onChange={() => setIsAcute(false)}
                    className="form-radio h-4 w-4 text-blue-400 border-white/30 bg-white/10 focus:ring-blue-400"
                  />
                  <span className="ml-2 text-gray-300">Chronic</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        <div className="mb-6">
          <h3 className="text-lg font-semibold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400 mb-3">
            Electrolytes (for Anion Gap)
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label htmlFor="sodium" className="block text-gray-300 mb-2 font-medium">
                Na+ (mEq/L)
              </label>
              <input
                type="number"
                id="sodium"
                name="sodium"
                value={values.sodium}
                onChange={handleChange}
                step="1"
                min="110"
                max="170"
                style={{
                  color: '#ffffff',
                  backgroundColor: 'rgba(0, 0, 0, 0.4)',
                  WebkitTextFillColor: '#ffffff'
                }}
                className="w-full p-3 border border-white/20 rounded-lg focus:outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-400/50 transition-all duration-300 backdrop-blur-sm"
                required
              />
            </div>

            <div>
              <label htmlFor="chloride" className="block text-gray-300 mb-2 font-medium">
                Cl- (mEq/L)
              </label>
              <input
                type="number"
                id="chloride"
                name="chloride"
                value={values.chloride}
                onChange={handleChange}
                step="1"
                min="70"
                max="130"
                style={{
                  color: '#ffffff',
                  backgroundColor: 'rgba(0, 0, 0, 0.4)',
                  WebkitTextFillColor: '#ffffff'
                }}
                className="w-full p-3 border border-white/20 rounded-lg focus:outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-400/50 transition-all duration-300 backdrop-blur-sm"
                required
              />
            </div>

            <div>
              <label htmlFor="albumin" className="block text-gray-300 mb-2 font-medium">
                Albumin (g/dL) <span className="text-gray-500">(optional)</span>
              </label>
              <input
                type="number"
                id="albumin"
                name="albumin"
                value={values.albumin}
                onChange={handleChange}
                step="0.1"
                min="1.0"
                max="7.0"
                style={{
                  color: '#ffffff',
                  backgroundColor: 'rgba(0, 0, 0, 0.4)',
                  WebkitTextFillColor: '#ffffff'
                }}
                className="w-full p-3 border border-white/20 rounded-lg focus:outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-400/50 transition-all duration-300 backdrop-blur-sm"
              />
            </div>
          </div>
        </div>

        <button
          type="submit"
          className="w-full bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 hover:from-blue-600 hover:via-purple-600 hover:to-pink-600 text-white font-bold py-3 px-6 rounded-lg shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-300"
        >
          Analyze ABG
        </button>
      </form>
    </div>
  );
};

export default ABGInputForm;
